import React, { useState } from 'react';
import { Input } from './input';
import { Label } from './label';
import { Loader2 } from 'lucide-react';
import { maskCEP } from '@/lib/format';
import { fetchAddressByCep, formatCepDisplay } from '@/services/cep.service';
import { toast } from 'sonner';

/**
 * Componente de input de CEP com busca automática de endereço
 * 
 * @param {Object} props
 * @param {string} props.value - Valor do CEP
 * @param {Function} props.onChange - Callback quando CEP muda
 * @param {Function} props.onAddressFound - Callback quando endereço é encontrado
 * @param {boolean} props.disabled - Se o input está desabilitado
 * @param {string} props.label - Label do input (padrão: "CEP")
 * @param {string} props.placeholder - Placeholder (padrão: "00000-000")
 * @param {boolean} props.required - Se o campo é obrigatório
 */
export function CepInput({ 
  value, 
  onChange, 
  onAddressFound, 
  disabled = false,
  label = "CEP",
  placeholder = "00000-000",
  required = false,
  className = "",
}) {
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const maskedValue = maskCEP(e.target.value);
    onChange(maskedValue);
  };

  const handleBlur = async (e) => {
    const cep = e.target.value.replace(/\D/g, '');
    
    if (cep.length !== 8) return;

    setLoading(true);
    
    try {
      const addressData = await fetchAddressByCep(cep);
      
      // Callback com os dados do endereço
      if (onAddressFound) {
        onAddressFound({
          zipCode: formatCepDisplay(addressData.cep),
          address: addressData.address,
          city: addressData.city,
          state: addressData.state,
          complement: addressData.complement,
          neighborhood: addressData.neighborhood,
        });
      }
      
      toast.success('Endereço encontrado!');
    } catch (error) {
      toast.error(error.message || 'Erro ao buscar CEP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={className}>
      {label && (
        <Label>
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      <div className="relative">
        <Input 
          value={value} 
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled || loading}
          placeholder={placeholder}
          maxLength={9}
        />
        {loading && (
          <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-blue-600" />
        )}
      </div>
      {loading && <span className="text-xs text-blue-600">Buscando endereço...</span>}
    </div>
  );
}
